import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import DomainHelper from "helpers/DomainHelper";
import axios from "../../utils/axios";

const baseUrl = DomainHelper.getAPIMain();

interface ApiKeyState {
  data: { apiKey: string }[];
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  isError: boolean;
  errorMessage: string | null;
}

const initialState: ApiKeyState = {
  data: [],
  isLoading: false,
  isCreating: false,
  isDeleting: false,
  isError: false,
  errorMessage: null,
};

// Read API keys
export const getApiKeys = createAsyncThunk(
  "apikey/read",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${baseUrl}client/api-key/read`, {
        withCredentials: true,
      });
      return thunkAPI.fulfillWithValue(response.data);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

// Create API key
export const createApiKey = createAsyncThunk(
  "apikey/create",
  async (_, thunkAPI) => {
    try {
      const response = await axios.post(
        `${baseUrl}client/api-key/create`,
        {},
        { withCredentials: true }
      );
      return thunkAPI.fulfillWithValue(response.data);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

// Delete API key
export const deleteApiKey = createAsyncThunk(
  "apikey/delete",
  async (_, thunkAPI) => {
    try {
      const response = await axios.delete(`${baseUrl}client/api-key/delete`, {
        withCredentials: true,
      });
      return thunkAPI.fulfillWithValue(response.data);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

const apiKeySlice = createSlice({
  name: "apikey",
  initialState,
  reducers: {
    resetState: (state) => {
      state.isError = false;
      state.errorMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get API keys
      .addCase(getApiKeys.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getApiKeys.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || [];
      })
      .addCase(getApiKeys.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload as string;
      })
      // Create API key
      .addCase(createApiKey.pending, (state) => {
        state.isCreating = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.isCreating = false;
        // Add the new API key to the list
        if (action.payload.data) {
          state.data.push({ apiKey: action.payload.data });
        }
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.isCreating = false;
        state.isError = true;
        state.errorMessage = action.payload as string;
      })
      // Delete API key
      .addCase(deleteApiKey.pending, (state) => {
        state.isDeleting = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteApiKey.fulfilled, (state) => {
        state.isDeleting = false;
        state.data = [];
      })
      .addCase(deleteApiKey.rejected, (state, action) => {
        state.isDeleting = false;
        state.isError = true;
        state.errorMessage = action.payload as string;
      });
  },
});

export const { resetState } = apiKeySlice.actions;
export default apiKeySlice.reducer;
